import { MEDIA } from '@/utils/CONST';
import { useGetList } from 'react-admin';
import { useMemo } from 'react';
import { composeMediaFmDownloadUrl, composeUri } from '@/utils/compose';
import { useGetOne } from 'react-admin';
import extractIdFromURI from '@/utils/id';
import { useQueryClient } from '@tanstack/react-query';

interface Props {
    resourceId: string | undefined;
    resourceSupported: string;
    tagsNames: string[];
}

const useGetLastMedia = ({ resourceId, resourceSupported, tagsNames }: Props) => {
    const queryClient = useQueryClient();

    const {
        data: fmData,
        isLoading: isLoadingFmData,
        refetch: refetchFmData,
    } = useGetList(
        'smartConnectView/file_system_mappers',
        {
            filter: {
                'parent.parent.name': `sc:folder-${resourceSupported}:${resourceId}`,
                lvl: 5,
                'tags.name': tagsNames,
            },
            meta: {
                basePath: 'fm',
            },
        },
        { enabled: resourceId !== undefined },
    );

    const mediaId = useMemo(
        () =>
            fmData !== undefined && fmData.length > 0
                ? composeUri(`${MEDIA}`, fmData[0].metadata['metadata.extra']['coreapi.mediaid'])
                : undefined,
        [fmData],
    );

    const { data: mediaData, isLoading: isLoadingMediaData } = useGetOne(
        MEDIA,
        {
            id: mediaId,
        },
        {
            enabled: mediaId !== undefined,
        },
    );

    const handleRemoveMediaDataQuery = () => {
        if (mediaId) {
            queryClient.removeQueries({ queryKey: [MEDIA, 'getOne', mediaId] });
        }
    };

    return {
        mediaData: mediaData
            ? {
                  ...mediaData,
                  downloadUrl: composeMediaFmDownloadUrl(
                      extractIdFromURI(mediaData.id) as string,
                      mediaData.contentUrlTokenAccess,
                  ),
              }
            : undefined,
        isLoading: isLoadingFmData || isLoadingMediaData,
        refetch: () => {
            handleRemoveMediaDataQuery();
            refetchFmData();
        },
    };
};

export default useGetLastMedia;
